import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { Button } from '@/components/ui/Button';
import { Establishment } from '@/types/domain.types';
import {
  Building2,
  Calendar,
  CheckCircle,
  Edit,
  MapPin,
  Plus,
  Star,
  XCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface EstablishmentsListProps {
  establishments: Establishment[];
  isLoading: boolean;
  error: unknown;
  onAddEstablishment?: () => void;
  onEditEstablishment?: (establishment: Establishment) => void;
}

export function EstablishmentsList({
  establishments,
  isLoading,
  error,
  onAddEstablishment,
  onEditEstablishment,
}: EstablishmentsListProps) {
  const { t } = useTranslation();

  const getStatusIcon = (isActive: boolean, isSynchro: boolean) => {
    if (!isActive) return <XCircle className="text-error-500 h-4 w-4" />;
    if (!isSynchro) return <Calendar className="text-warning-500 h-4 w-4" />;
    return <CheckCircle className="text-success-500 h-4 w-4" />;
  };

  const getStatusText = (isActive: boolean, isSynchro: boolean) => {
    if (!isActive) return t('common.inactive', 'Inactif');
    if (!isSynchro) return t('common.notSynced', 'Non synchronisé');
    return t('common.active', 'Actif');
  };

  const getStatusColor = (isActive: boolean, isSynchro: boolean) => {
    if (!isActive) return 'text-error-600 bg-error-50';
    if (!isSynchro) return 'text-warning-600 bg-warning-50';
    return 'text-success-600 bg-success-50';
  };

  const formatAddress = (establishment: Establishment) => {
    const parts = [
      establishment.adressPostale,
      establishment.adressPostale2,
      establishment.complementAdress,
      `${establishment.codePostal} ${establishment.ville}`,
      establishment.pays,
    ].filter(Boolean);

    return parts.join('\n');
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <LoadingSkeleton className="h-6 w-48" />
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <LoadingSkeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-50 rounded-lg border border-red-200 p-6 text-center">
        <XCircle className="text-error-500 mx-auto mb-2 h-8 w-8" />
        <h3 className="text-error-900 mb-2 text-lg font-semibold">
          {t('establishments.error.title', 'Erreur de chargement')}
        </h3>
        <p className="text-error-700 text-sm">
          {t(
            'establishments.error.description',
            'Impossible de charger les établissements'
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
            <Building2 className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">
              {t('establishments.title', 'Établissements')}
            </h3>
            <p className="text-sm text-neutral-500">
              {t(
                'establishments.description',
                'Établissements liés à ce dossier'
              )}
            </p>
          </div>
        </div>
        {onAddEstablishment && (
          <Button
            variant="outline"
            size="sm"
            onClick={onAddEstablishment}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('establishments.add', 'Ajouter')}
          </Button>
        )}
      </div>

      {/* Establishments List */}
      {establishments.length === 0 ? (
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-6 text-center">
          <Building2 className="mx-auto mb-2 h-8 w-8 text-neutral-400" />
          <h3 className="mb-2 text-sm font-medium text-neutral-900">
            {t('establishments.empty.title', 'Aucun établissement')}
          </h3>
          <p className="mb-4 text-sm text-neutral-600">
            {t(
              'establishments.empty.description',
              "Aucun établissement n'est lié à ce dossier"
            )}
          </p>
          {onAddEstablishment && (
            <Button variant="outline" size="sm" onClick={onAddEstablishment}>
              <Plus className="mr-2 h-4 w-4" />
              {t('establishments.addFirst', 'Ajouter le premier établissement')}
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {establishments.map(establishment => (
            <div
              key={establishment.uuid}
              className="rounded-lg border border-neutral-200 bg-white p-4 transition-colors hover:bg-neutral-50"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  {/* Header */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-neutral-100">
                      <Building2 className="h-4 w-4 text-neutral-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-neutral-900">
                          {establishment.raisonSociale}
                        </h4>
                        {establishment.etablissementPrincipal && (
                          <Star className="text-warning-500 h-4 w-4" />
                        )}
                      </div>
                      <p className="text-sm text-neutral-500">
                        Code: {establishment.code} | SIRET:{' '}
                        {establishment.siret}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(
                        establishment.isActive,
                        establishment.isSynchro
                      )}
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(
                          establishment.isActive,
                          establishment.isSynchro
                        )}`}
                      >
                        {getStatusText(
                          establishment.isActive,
                          establishment.isSynchro
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="grid gap-3 sm:grid-cols-2">
                    {/* Address */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-neutral-500">
                        <MapPin className="h-4 w-4" />
                        <span className="font-medium">
                          {t('establishments.address', 'Adresse')}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-line text-neutral-700">
                        {formatAddress(establishment)}
                      </p>
                    </div>

                    {/* Activity */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-neutral-500">
                        <Building2 className="h-4 w-4" />
                        <span className="font-medium">
                          {t('establishments.activity', 'Activité')}
                        </span>
                      </div>
                      <p className="text-sm text-neutral-700">
                        {establishment.activite}
                        <br />
                        <span className="text-xs text-neutral-500">
                          APE: {establishment.ape}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="flex items-center gap-4 text-sm text-neutral-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {t('establishments.created', 'Créé le')}{' '}
                        {new Date(establishment.createdAt).toLocaleDateString(
                          'fr-FR'
                        )}
                      </span>
                    </div>
                    {establishment.lastSyncAt && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {t('establishments.lastSync', 'Dernière sync')}{' '}
                          {new Date(
                            establishment.lastSyncAt
                          ).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                {onEditEstablishment && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEditEstablishment(establishment)}
                    className="ml-4 flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    {t('common.edit', 'Modifier')}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
